// src/pages/Lessons.jsx
import React, { useState, useEffect } from "react";
import { Lesson, UserProgress, User } from "@/api/entities";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Play, Lock, CheckCircle2, Clock, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

/** Prova a leggere il catalogo centralizzato. Ritorna [] se qualcosa va storto. */
async function fetchServerCatalog() {
  try {
    const res = await fetch(`/api/content?ts=${Date.now()}`, {
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    const lessons = Array.isArray(data?.lessons) ? data.lessons : [];
    // normalizza ordine a numero
    return lessons.map((l, i) => ({ ...l, order: Number(l.order ?? i + 1) }));
  } catch {
    return [];
  }
}

/** Deduplica per id mantenendo l’ultima occorrenza. */
function dedupeById(list) {
  const map = new Map();
  for (const l of list) if (l?.id != null) map.set(l.id, l);
  return Array.from(map.values());
}

export default function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [userProgress, setUserProgress] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // ok anche se non loggato; forziamo solo la cache utente se disponibile
        try { await User.me(); } catch {}

        // 1) prova server
        let catalog = await fetchServerCatalog();

        // 2) se il server risponde, usiamo quello e lo salviamo come fallback locale
        if (catalog.length > 0) {
          localStorage.setItem("RB_Lesson", JSON.stringify(catalog));
          localStorage.setItem("RB_CATALOG_VER", String(Date.now()));
        } else {
          // 3) server vuoto/non raggiungibile → fallback store/app
          const all = await Lesson.list();
          catalog = Array.isArray(all) ? all : [];
        }

        // 4) deduplica + sort robusto
        const deduped = dedupeById(catalog);
        const sorted = deduped
          .map((l) => ({ ...l, order: Number(l.order || 0) }))
          .sort((a, b) => a.order - b.order);

        const progressList = await UserProgress.list();
        setLessons(sorted);
        setUserProgress(progressList || []);
        // debug opzionale
        // window.__RB_LESSONS = sorted;
      } catch (e) {
        console.error("Errore caricamento lezioni:", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // ⬇️ NEW: ricarica i progressi quando una lezione/esercizio salva (es. lezione 6 emette "rb:checkin-saved")
  useEffect(() => {
    const reloadProgress = async () => {
      try {
        const list = await UserProgress.list();
        setUserProgress(list || []);
      } catch (e) {
        console.warn("Reload progress fallito:", e);
      }
    };
    const onSaved = () => reloadProgress();
    const onFocus = () => reloadProgress();

    window.addEventListener("rb:checkin-saved", onSaved);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("rb:checkin-saved", onSaved);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // ---------- helpers progresso ----------
  const isRecordCompleted = (r) =>
    !!(
      r?.completed ||
      r?.is_complete ||
      r?.done ||
      r?.status === "completed" ||
      (typeof r?.progress === "number" && r.progress >= 100)
    );

  const matchesLesson = (r, lesson) => {
    if (!r || !lesson) return false;
    const idOk =
      r.lesson_id === lesson.id ||
      r.lessonId === lesson.id ||
      r.lesson === lesson.id;
    const slugOk = r.slug && lesson.slug && String(r.slug) === String(lesson.slug);
    return idOk || slugOk;
  };

  const interpretProgress = (r) => {
    const lessonCompleted = !!(
      r?.lesson_completed ||
      r?.type === "lesson_completed" ||
      (r?.type === "lesson" && isRecordCompleted(r)) ||
      (isRecordCompleted(r) && !r?.exercise_completed && r?.type == null)
    );
    const exerciseCompleted = !!(
      r?.exercise_completed ||
      r?.exercise_done ||
      r?.type === "exercise_completed" ||
      (r?.type === "exercise" && isRecordCompleted(r)) ||
      r?.exercise_answer
    );
    return { lessonCompleted, exerciseCompleted };
  };

  const getAggregatedProgress = (lesson) => {
    const recs = userProgress.filter((p) => matchesLesson(p, lesson));
    if (recs.length === 0) return { lessonCompleted: false, exerciseCompleted: false };
    let lessonCompleted = false;
    let exerciseCompleted = false;
    for (const r of recs) {
      const it = interpretProgress(r);
      lessonCompleted = lessonCompleted || it.lessonCompleted;
      exerciseCompleted = exerciseCompleted || it.exerciseCompleted;
      if (lessonCompleted && exerciseCompleted) break;
    }
    return { lessonCompleted, exerciseCompleted };
  };

  const isLessonUnlocked = (lesson) => {
    const orderNum = Number(lesson.order || 0);
    if (orderNum <= 1) return true;
    const previous = lessons.find((l) => Number(l.order || 0) === orderNum - 1);
    if (!previous) return true;
    const { lessonCompleted, exerciseCompleted } = getAggregatedProgress(previous);
    return lessonCompleted && exerciseCompleted;
  };

  const getLessonStatus = (lesson) => {
    const { lessonCompleted, exerciseCompleted } = getAggregatedProgress(lesson);
    if (!isLessonUnlocked(lesson)) return "locked";
    if (lessonCompleted && exerciseCompleted) return "completed";
    if (lessonCompleted && !exerciseCompleted) return "exercise_pending";
    return "unlocked";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Percorso di{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Apprendimento
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Completa le lezioni in ordine per sbloccare nuovi contenuti e far progredire le tue conoscenze
          </p>
        </motion.div>

        <div className="space-y-4">
          {lessons.map((lesson, index) => {
            const status = getLessonStatus(lesson);
            const isUnlocked = status !== "locked";
            const statusMap = {
              completed: "Completato",
              exercise_pending: "Esercizio in attesa",
              locked: "Bloccato",
              unlocked: "Pronto per iniziare",
            };

            return (
              <motion.div key={lesson.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06 }}>
                <Card
                  className={`transition-all duration-300 ${
                    status === "completed"
                      ? "bg-gradient-to-r from-green-50 to-green-100 border-green-200 shadow-lg"
                      : isUnlocked
                      ? "bg-white hover:shadow-xl hover:scale-[1.02] shadow-lg"
                      : "bg-gray-50 border-gray-200"
                  } ${isUnlocked ? "" : "cursor-not-allowed"}`}
                >
                  <div
                    className={isUnlocked ? "cursor-pointer" : ""}
                    onClick={() => {
                      if (isUnlocked) navigate(createPageUrl(`Lesson?id=${lesson.id}`));
                    }}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                              status === "completed" ? "bg-green-500" : isUnlocked ? "bg-gradient-to-r from-blue-500 to-purple-600" : "bg-gray-400"
                            }`}
                          >
                            {status === "completed" ? (
                              <CheckCircle2 className="w-6 h-6 text-white" />
                            ) : status === "locked" ? (
                              <Lock className="w-6 h-6 text-white" />
                            ) : (
                              <Play className="w-6 h-6 text-white" />
                            )}
                          </div>
                          <div>
                            <CardTitle className={`text-xl ${status === "locked" ? "text-gray-500" : "text-gray-900"}`}>
                              Lezione {Number(lesson.order || 0)}: {lesson.title}
                            </CardTitle>
                            <div className="flex items-center gap-3 mt-1">
                              {lesson.duration && (
                                <div className="flex items-center gap-1 text-gray-500">
                                  <Clock className="w-4 h-4" />
                                  <span className="text-sm">{lesson.duration} min</span>
                                </div>
                              )}
                              <Badge
                                variant={
                                  status === "completed"
                                    ? "default"
                                    : status === "exercise_pending"
                                    ? "secondary"
                                    : isUnlocked
                                    ? "outline"
                                    : "secondary"
                                }
                              >
                                {statusMap[status]}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        {isUnlocked && (
                          <Link to={createPageUrl(`Lesson?id=${lesson.id}`)} onClick={(e) => e.stopPropagation()}>
                            <Button
                              className={
                                status === "completed"
                                  ? "bg-green-600 hover:bg-green-700"
                                  : "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                              }
                            >
                              {status === "completed" ? "Rivedi" : "Inizia"}
                            </Button>
                          </Link>
                        )}
                      </div>
                    </CardHeader>

                    {lesson.description && (
                      <CardContent>
                        <p className={`${status === "locked" ? "text-gray-400" : "text-gray-600"}`}>{lesson.description}</p>
                      </CardContent>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {lessons.length === 0 && !isLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessuna lezione disponibile</h3>
            <p className="text-gray-500">Le lezioni appariranno qui una volta pubblicate.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
